import { ProposalDAOInterface, ProposalDataDAOInterface } from '../ProposalDAO';
import { BaseProposalDAO } from '../ProposalDAOImpl';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { getPortfoliosForView, createAggregatedPortfolio } from '../../utils/client-level-summary';
import { CLIENT_LEVEL_VIEW } from '@/enums/ClientLevelView';
import DonutData from '@/types/donut-data';
import SeriesData from '@/types/series-data';
import LineSeriesData from '@/types/line-series-data';
import ScatterPlotData from '@/types/scatter-plot-data';
import IndustryHoldingsDAO from './IndustryHoldingsDAO';

export default class AggregatedTopEquityIndustryHoldingsDAO extends BaseProposalDAO implements ProposalDataDAOInterface {
  private industryHoldingsDAO: IndustryHoldingsDAO | null = null;

  private view: CLIENT_LEVEL_VIEW = CLIENT_LEVEL_VIEW.CURRENT;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new AggregatedTopEquityIndustryHoldingsDAO(proposal, proposalElement);
  }

  protected build(): void {
    const daoConfig: ProposalNS.AggregatedTopEquityIndustryHoldingsConfig = this.proposalElement.DAO as ProposalNS.AggregatedTopEquityIndustryHoldingsConfig;

    // Extract view configuration
    this.view = daoConfig.view;

    // Step 1: Get portfolios for the view
    const portfolios = getPortfoliosForView(this.proposal, this.view);

    if (portfolios.length === 0) {
      return;
    }

    // Step 2: Create aggregated portfolio
    const aggregatedPortfolio = createAggregatedPortfolio(portfolios, this.view);

    // Step 3: Create modified proposal with aggregated portfolio
    const aggregatedProposal: ProposalNS.Proposal = {
      ...this.proposal,
      items: [aggregatedPortfolio],
    };

    // Step 4: Create IndustryHoldingsDAO with aggregated proposal
    // Pass through all IndustryHoldingsDAO config (filtering, grouping, etc.)
    const industryHoldingsConfig: ProposalNS.IndustryHoldingsConfig = {
      type: ProposalNS.DAOType.IndustryHoldings,
      filterTopXDAOOptions: daoConfig.filterTopXDAOOptions,
    };

    const industryHoldingsElement: ProposalElementNS.ProposalDataElement = {
      ...this.proposalElement,
      DAO: industryHoldingsConfig,
    };

    this.industryHoldingsDAO = IndustryHoldingsDAO.buildInstance(aggregatedProposal, industryHoldingsElement) as IndustryHoldingsDAO;
    this.industryHoldingsDAO.buildDAO();
  }

  // eslint-disable-next-line class-methods-use-this
  protected postBuild(): void {
    // No post-build logic needed - IndustryHoldingsDAO handles everything
  }

  /**
   * Get donut chart data for aggregated top equity industry holdings
   *
   * Returns top N industries + "Other" slice for remaining industries
   * Industry weights are pre-calculated in createAggregatedPortfolio to sum to 100%
   * This method filters to top N and adds "Other" for the remainder
   *
   * @param index Portfolio index (default 0, only one aggregated portfolio exists)
   * @returns DonutData with top industries + "Other" or null if no data available
   */
  public getDonutData(index: number = 0): DonutData | null {
    if (!this.industryHoldingsDAO) {
      return null;
    }

    const donutData = this.industryHoldingsDAO.getDonutData(index);

    if (!donutData || !donutData.data || !donutData.data[0]) {
      return donutData;
    }

    // Calculate "Other" slice for industries not in top N
    const seriesData = donutData.data[0];
    const topNWeight = seriesData.reduce((sum, item) => sum + (item.y || 0), 0);

    if (topNWeight < 100 && topNWeight > 0) {
      const otherWeight = 100 - topNWeight;
      seriesData.push({
        name: `${this.formatMessage('Comparison.Table.AssetDetails.Other')}*`,
        y: otherWeight,
        key: 'OTHER',
      });
    }

    return donutData;
  }

  /**
   * Get the underlying IndustryHoldingsDAO for advanced usage
   */
  public getIndustryHoldingsDAO(): IndustryHoldingsDAO | null {
    return this.industryHoldingsDAO;
  }

  // eslint-disable-next-line class-methods-use-this
  protected getColorKeys(): string[] {
    return [];
  }

  // Implement ProposalDataDAOInterface methods
  // eslint-disable-next-line class-methods-use-this
  public getSeriesData(): SeriesData[][] {
    // Not used for donut charts
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  public getGraphConfigOptions(): ProposalNS.ChartConfigOptions {
    // Not used for donut charts
    return {};
  }

  // eslint-disable-next-line class-methods-use-this
  public getLineSeries(): LineSeriesData[] {
    // Not used for donut charts
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  public getScatterSeries(): ScatterPlotData[] {
    // Not used for donut charts
    return [];
  }
}
